import { useQuery } from '@tanstack/react-query'
import { useEffect, useSyncExternalStore } from 'react'

import { subscribeToRealtimeEvents } from '@/websockets/Client/realtimeEventsClient.ts'
import type { UpdaterTransferChangedEvent } from '@/websockets/Types/events.ts'

import { getUpdateTransferStatusesHandler } from '../server.ts'
import type { UpdateTransferQueueStatus } from '../server.ts'
import type {
    StartTransferOptions,
    StartUpdateTransferOptions,
    TransferOverlayLogicReturn,
    TransferStatus,
    TransferTask,
} from '../Types/types'
import { mergeUpdateTransferQueueStatus } from '../Utils/updateTransferStatus.ts'

const listeners = new Set<() => void>()
const dismissedUpdateTransferIds = new Set<string>()
const updateTransferRemovalTimeouts = new Map<string, number>()
const UPDATE_TRANSFER_REMOVAL_DELAY_MS = 8000
let tasks: Array<TransferTask> = []

function emit() {
    listeners.forEach((listener) => listener())
}

function updateTask(id: string, updater: (task: TransferTask) => TransferTask) {
    const nextTasks = tasks.map((task) =>
        task.id === id ? updater(task) : task,
    )

    if (nextTasks.every((task, index) => task === tasks[index])) {
        return
    }

    tasks = nextTasks
    emit()
}

function removeTask(id: string) {
    const removalTimeout = updateTransferRemovalTimeouts.get(id)

    if (removalTimeout !== undefined) {
        window.clearTimeout(removalTimeout)
        updateTransferRemovalTimeouts.delete(id)
    }

    tasks = tasks.filter((task) => task.id !== id)
    emit()
}

function scheduleUpdateTransferRemoval(id: string) {
    if (updateTransferRemovalTimeouts.has(id)) {
        return
    }

    const timeout = window.setTimeout(
        () => removeTask(id),
        UPDATE_TRANSFER_REMOVAL_DELAY_MS,
    )
    updateTransferRemovalTimeouts.set(id, timeout)
}

function createTransferId() {
    return crypto.randomUUID()
}

function subscribeToTransferTasks(listener: () => void) {
    listeners.add(listener)

    return function unsubscribe() {
        listeners.delete(listener)
    }
}

function getTransferTasksSnapshot() {
    return tasks
}

export function dismissTransferTask(id: string) {
    const task = tasks.find((currentTask) => currentTask.id === id)

    if (task?.kind === 'update') {
        dismissedUpdateTransferIds.add(id)
    }

    removeTask(id)
}

export function startTransferTask({
    direction,
    title,
    totalBytes,
}: StartTransferOptions) {
    const id = createTransferId()
    const safeTotalBytes = Math.max(totalBytes, 1)
    const startedAt = Date.now()
    let lastTickAt = startedAt
    let lastTransferredBytes = 0

    tasks = [
        ...tasks,
        {
            id,
            kind: 'file',
            direction,
            title,
            totalBytes: safeTotalBytes,
            transferredBytes: 0,
            speedBytesPerSecond: 0,
            progress: 0,
            status: 'running',
        },
    ]
    emit()

    const interval = window.setInterval(function () {
        const now = Date.now()
        const elapsed = now - startedAt
        const nextProgress = Math.min(
            92,
            Math.max(4, (1 - Math.exp(-elapsed / 1800)) * 92),
        )
        const nextTransferredBytes = Math.round(
            (safeTotalBytes * nextProgress) / 100,
        )
        const tickSeconds = Math.max((now - lastTickAt) / 1000, 0.001)
        const speedBytesPerSecond = Math.max(
            0,
            (nextTransferredBytes - lastTransferredBytes) / tickSeconds,
        )

        lastTickAt = now
        lastTransferredBytes = nextTransferredBytes

        updateTask(id, (task) => {
            if (task.status !== 'running') {
                return task
            }

            return {
                ...task,
                transferredBytes: nextTransferredBytes,
                speedBytesPerSecond,
                progress: nextProgress,
            }
        })
    }, 250)

    function finish(
        status: Exclude<TransferStatus, 'running'>,
        message?: string,
    ) {
        window.clearInterval(interval)

        updateTask(id, (task) => {
            if (task.kind !== 'file') return task

            return {
                ...task,
                transferredBytes:
                    status === 'success'
                        ? task.totalBytes
                        : task.transferredBytes,
                speedBytesPerSecond:
                    status === 'success' ? 0 : task.speedBytesPerSecond,
                progress: status === 'success' ? 100 : task.progress,
                status,
                message,
            }
        })

        if (status === 'success' || status === 'error') {
            window.setTimeout(() => removeTask(id), 3500)
        }
    }

    return {
        id,
        complete(message?: string) {
            finish('success', message)
        },
        fail(message?: string) {
            finish('error', message)
        },
        dismiss() {
            window.clearInterval(interval)
            removeTask(id)
        },
    }
}

function getUpdateTransferProgress(
    stage: UpdaterTransferChangedEvent['payload']['stage'],
    step: number,
    totalSteps: number,
) {
    if (stage === 'completed') return 100
    if (stage === 'queued') return 5

    const safeTotalSteps = Math.max(totalSteps, 1)
    const stepStart = ((Math.max(step, 1) - 1) / safeTotalSteps) * 100

    return Math.min(95, stepStart + 45 / safeTotalSteps)
}

export function startUpdateTransferTask({
    id,
    websiteId,
    extensionId,
    websiteName,
    extensionName,
    totalSteps,
}: StartUpdateTransferOptions) {
    if (
        dismissedUpdateTransferIds.has(id) ||
        tasks.some((task) => task.id === id)
    ) {
        return
    }

    tasks = [
        ...tasks,
        {
            id,
            kind: 'update',
            title: `${websiteName} – ${extensionName}`,
            websiteId,
            extensionId,
            step: 0,
            totalSteps,
            progress: 5,
            status: 'running',
            stage: 'queued',
            message: 'Update wurde in die Warteschlange eingereiht.',
        },
    ]
    emit()
}

function applyUpdaterTransferEvent(
    payload: UpdaterTransferChangedEvent['payload'],
) {
    if (dismissedUpdateTransferIds.has(payload.transferId)) {
        if (payload.stage === 'completed' || payload.stage === 'failed') {
            window.setTimeout(
                () => dismissedUpdateTransferIds.delete(payload.transferId),
                60_000,
            )
        }

        return
    }

    startUpdateTransferTask({
        id: payload.transferId,
        websiteId: payload.websiteId,
        extensionId: payload.extensionId,
        websiteName: payload.websiteName,
        extensionName: payload.extensionName,
        totalSteps: payload.totalSteps,
    })

    updateTask(payload.transferId, (task) => {
        if (task.kind !== 'update') return task

        return {
            ...task,
            step: payload.step,
            totalSteps: payload.totalSteps,
            progress:
                payload.progress ??
                getUpdateTransferProgress(
                    payload.stage,
                    payload.step,
                    payload.totalSteps,
                ),
            status:
                payload.stage === 'completed'
                    ? 'success'
                    : payload.stage === 'failed'
                      ? 'error'
                      : 'running',
            stage: payload.stage,
            message: payload.error ?? payload.message,
        }
    })

    if (payload.stage === 'completed' || payload.stage === 'failed') {
        scheduleUpdateTransferRemoval(payload.transferId)
    }
}

function applyUpdateTransferQueueStatus(status: UpdateTransferQueueStatus) {
    if (!tasks.some((task) => task.id === status.id)) {
        return
    }

    updateTask(status.id, (task) => {
        if (task.kind !== 'update') return task
        return mergeUpdateTransferQueueStatus(task, status)
    })

    if (status.status === 'completed' || status.status === 'failed') {
        scheduleUpdateTransferRemoval(status.id)
    }
}

export function useUpdaterTransferOverlayRealtime() {
    const transferTasks = useSyncExternalStore(
        subscribeToTransferTasks,
        getTransferTasksSnapshot,
        getTransferTasksSnapshot,
    )
    const runningUpdateTransferIds = transferTasks.flatMap((task) =>
        task.kind === 'update' && task.status === 'running' ? [task.id] : [],
    )
    const transferStatusesQuery = useQuery({
        queryKey: ['update-transfer-statuses', runningUpdateTransferIds],
        queryFn: () =>
            getUpdateTransferStatusesHandler({
                data: { ids: runningUpdateTransferIds },
            }),
        enabled: runningUpdateTransferIds.length > 0,
        refetchInterval: 2000,
    })

    useEffect(
        () =>
            subscribeToRealtimeEvents((event) => {
                if (event.type !== 'updater-transfer.changed') return
                applyUpdaterTransferEvent(event.payload)
            }),
        [],
    )

    useEffect(() => {
        transferStatusesQuery.data?.forEach(applyUpdateTransferQueueStatus)
    }, [transferStatusesQuery.data])
}

export function getDataUrlByteSize(dataUrl: string) {
    const base64 = dataUrl.split(',')[1] ?? ''
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0

    return Math.max(0, Math.floor((base64.length * 3) / 4) - padding)
}

export default function useTransferOverlayTasks(): TransferOverlayLogicReturn {
    const transferTasks = useSyncExternalStore(
        subscribeToTransferTasks,
        getTransferTasksSnapshot,
        getTransferTasksSnapshot,
    )

    function handleDismissTask(id: string) {
        dismissTransferTask(id)
    }

    return {
        state: {
            tasks: transferTasks,
        },
        handler: {
            handleDismissTask,
        },
    }
}
